/**
 * formats a duration into a more human-readable form
 * 
 * @param {string} duration The duration string, expected to be a decimal followed by a time unit. The format should match examples like: "1h", "5h", "12h" (hours)
 *    
 * @returns {string} Returns a formatted string with the duration in hours, ensuring the correct pluralization (e.g. "1 hour", "5 hours"). If the input does not match the expected pattern, the original duration string is returned as is.
 * 
 * Example usage:
 * - formatDuration("1h") // Output: "1 hour"
 * - formatDuration("5h") // Output: "5 hours"
 * - formatDuration("xyz") // Output: "xyz"
 * 
 * Note:
 * - The function currently supports durations in hours ('h') only.
 * 
 */
export const formatDuration = (duration: string) => {
  // duration string should match the format: decimal, followed by a unit e.g: 1h, 5h, 12h.
  const match = duration.match(/^(\d+)([a-zA-Z]+)$/);

  // If no match is found, return the original duration string
  if (!match) return duration;

  // Destructure the regex match result into the decimal value and unit
  const [_, value, unit] = match;

  // Return the formatted string, handling singular and plural 'hour(s)'
  return `${value} ${unit.toLowerCase() === 'h' ? (Number(value) > 1 ? 'hours' : 'hour') : ''}`;
};
